import classNames from 'classnames';
import Color from 'color';

import styles from './styles.less';

function clamp(v, mn, mx) {
    return Math.max(Math.mnin(v, mx), mn);
}

function defaultComparator (a, b) { return a < b; }

//TODO factor out
export function resolveColor({colorEncoding, columns = [], defaultColor}) {

    if (!colorEncoding) {
        return defaultColor;
    }

    const attr = colorEncoding.attribute;
    const colMaybe = columns.filter(({ key }) => key === attr);
    const val = colMaybe.length ? colMaybe[0].value : undefined;
    
    if (colorEncoding.asText) {
        return val ? new Color(val).toString() : defaultColor;
    } else if (colorEncoding.mapping) {
        if (colorEncoding.mapping.categorical) {
            const { fixed, other = defaultColor } = colorEncoding.mapping.categorical;
            const v = val in fixed  ? fixed[val] : other;
            return v ? new Color(v).toString() : defaultColor;
        } else if (colorEncoding.mapping.continuous) {
            const { bins = [], other, comparator } = colorEncoding.mapping.continuous;
            const op = comparator == '<=' ? (a, b) => (a <= b)
                : comparator == '<' ? (a, b) => (a < b)
                : defaultComparator;
            if (typeof(val) === 'number') {
                for (const [ sentinel, binding ] of bins) {
                    if ((sentinel == null) || op(val, sentinel)) {
                        return new Color(binding).toString();
                    }
                }
            }
            return other;
        } else {
            //should not be reachable
            return defaultColor;
        }
    } else if (typeof(val) === 'string') {
        return new Color(val).toString();
    }   
}

//TODO factor out
export function resolveIconClass({iconEncoding, columns = []}) {

    const attr = iconEncoding.attribute;
    const colMaybe = columns.filter(({ key }) => key === attr);
    const val = colMaybe.length ? colMaybe[0].value : undefined;

    if (iconEncoding.mapping) {
        if (iconEncoding.mapping.categorical) {
            const { fixed, other = undefined } = iconEncoding.mapping.categorical;
            const valStr = colMaybe.length ? String(val) : undefined;
            const v = valStr in fixed ? fixed[valStr] : other;
            if (iconEncoding.asText) {
                return v !== undefined && v !== null ? String(v) : undefined;
            } else {
                return coerceIconStr(v, iconEncoding.asText === true);
            }
        } else if (iconEncoding.mapping.continuous) {
            const { bins = [], other, comparator = defaultComparator } = iconEncoding.mapping.continuous;
            const op = comparator == '<=' ? (a, b) => (a <= b)
                : comparator == '<' ? (a, b) => (a < b)
                : defaultComparator;
            if (typeof(val) === 'number') {
                for (const [ sentinel, binding ] of bins) {
                    if ((sentinel == null) || op(val, sentinel)) {
                        if (iconEncoding.asText) {
                            return binding !== undefined && binding !== null ? String(binding) : undefined;
                        } else {
                            return coerceIconStr(binding);
                        }
                    }
                }
            }
            if (iconEncoding.asText) {
                return other !== undefined && other !== null ? String(other) : undefined;
            } else {
                return coerceIconStr(other);
            }
        } else {
            return undefined;
        }
    } else {
        return colMaybe.length && val !== undefined && val !== null ? String(val) : undefined;
    }

}


//str -> ? str<fa | url | data>
function coerceIconStr(s) {
    return !s ? undefined
        : isImageURI(s) || isFontAwesome(s) || isDataURI(s) || isFlag(s) ? s
        : isFontAwesomeSuffix(s) ? `fa-${s}`
        : undefined;
}


function isFontAwesomeSuffix(s = '') {
    return s.match(/^[a-zA-Z0-9-]+$/)
}

function isFontAwesome(s = '') {
    return s.startsWith('fa-') || s.startsWith('fas-');
}

function isDataURI(s = '') {
    return s.startsWith('data:');
}

function isImageURI(s = '') {
    return s.startsWith('http://') || s.startsWith('https://');
}

function isFlag(s = '') {
    return s.startsWith('flag-icon-');
}


// str -> ? 'fa' | 'imageURI' | 'dataURI' | 'faSuffix' | 'flag'
export function inferIconType(s = '') {
    if (!s) {
        return undefined;
    } else if (isFontAwesome(s)) {
        return 'fa';
    } else if (isImageURI(s)) {
        return 'imageURI';
    } else if (isDataURI(s)) {
        return 'dataURI';
    } else if (isFlag(s)) {
        return 'flag';
    } else if (isFontAwesomeSuffix(s)) { //last as generic alpha numeric
        return 'faSuffix';
    } else {
        return undefined;
    }
}



function filterStr ({style}) {
    const filters = {};
    if (style) {
        ['opacity', 'grayscale', 'brightness', 'contrast', 'invert', 'saturate'].forEach(p => {
            if (p in style) {
                const v = +(style[p]);
                if (!isNaN(v)) {
                    filters[p] = `${p}(${clamp(v, 0, 1)})`;
                }
            }
        });
        if ('hueRotate' in style) {
            const v = +(style['hueRotate']);
            if (!isNaN(v)) {
                filters['hueRotate'] = `hue-rotate(${clamp(Math.round(v), 0, 360)}deg)`;
            }
        }
        return  Object.values(filters).join(' ');
    } else {
        return '';
    }

}

function maybeColorProp(color) {
    return color === undefined || color === null ? {} : { color };
}

export function IconElt({iconClass, shape, asText, style, colorEncoding, columns}) {

    const filter = style ? {'filter': filterStr({style})} : {};

    if (asText) {
        if (iconClass === null || iconClass === undefined || iconClass === '') {
            return null;
        } else {
            return (<span
                    className={styles[`${iconClass.length <= 3 ? 'short' : 'long'}-text`]}
                    style={{...filter, ...maybeColorProp(resolveColor({colorEncoding, columns}))}} >{
                iconClass
            }</span>);
        }
    }

    const iconType = inferIconType(iconClass);

    if (!iconType) { return null; }

    switch (iconType) {
        case 'imageURI':
            return <img src={iconClass} className={styles['icon-img-uri']} style={filter} />;
        case 'dataURI':
            return <img src={iconClass} className={styles['icon-data-uri']} style={filter} />;
        case 'fa':
            return (<i className={classNames({
                'fa': true,
                'fa-fw': true,
                [iconClass]: true
            })} style={{...filter, ...maybeColorProp(resolveColor({colorEncoding, columns})) }} />);
        case 'faSuffix':
            return (<i className={classNames({
                'fa': true,
                'fa-fw': true,
                [`fa-${iconClass}`]: true
            })} style={{...filter, ...maybeColorProp(resolveColor({colorEncoding, columns})) }} />);
        case 'flag':
            return (<span className={classNames({
                'flag-icon': true,
                'flag-icon-squared': shape === 'circle',
                [iconClass]: true
            })} style={{...filter }} />);
        case undefined:
        default:
            return null;
    }
}
